import { readonly, writable } from "svelte/store"
import { parseContextSession, type ContextSessionResponse } from "@kontextwerk/control-web-client"
import type { createContextRootClient } from "@kontextwerk/control-web-frame/shared/logic/context-runtime"
import { createContextPlanningApi } from "../../../../../../../../../api/kehrblatt/planning-context"
import type {
  KehrblattPlanningSettings,
  PlanningSettingsResponse,
} from "../../../../../../../../../api/kehrblatt/planning"
import { contextPlanningPage } from "../../../config"

type State = {
  status: "idle" | "loading" | "ready" | "denied" | "invalidated" | "error"
  settings: PlanningSettingsResponse | null
}
/** Owns one bound page; the form owns its draft and never recaptures at submission. */
export const createContextPlanning = (
  client: ReturnType<typeof createContextRootClient>,
  value: ContextSessionResponse,
  organizationId: string,
) => {
  const session = parseContextSession(value)
  const page = contextPlanningPage(session, organizationId)
  let state: State = { status: page ? "idle" : "denied", settings: null }
  const store = writable(state)
  const capture = page ? client.root.captureMutation() : null
  const local = new AbortController()
  let disposed = false
  let generation = 0
  const set = (next: State) => {
    state = next
    store.set(next)
  }
  const invalidate = () => {
    local.abort()
    generation += 1
    set({ status: "invalidated", settings: null })
  }
  const current = () =>
    !disposed &&
    !local.signal.aborted &&
    !!capture?.requestOptions.isCurrent() &&
    !!contextPlanningPage(session, organizationId)
  const api = capture
    ? createContextPlanningApi(client.operations, session, {
        ...capture.requestOptions,
        signal: local.signal,
        isCurrent: current,
      })
    : null
  capture?.requestOptions.signal.addEventListener("abort", invalidate)

  if (capture?.requestOptions.signal.aborted) invalidate()

  const failure = (error: unknown) => {
    if (!current()) {
      invalidate()

      return
    }

    if ([401, 403, 409].includes((error as { statusCode?: number })?.statusCode ?? 0)) {
      client.root.invalidate("revoked")
      invalidate()
    } else set({ status: "error", settings: null })
  }

  return {
    state: readonly(store),
    canManage: page?.canManage ?? false,
    async load() {
      if (!api || !current() || state.status === "loading") return

      const ticket = ++generation
      set({ status: "loading", settings: null })
      try {
        const result = await api.load()

        if (ticket === generation && current()) set({ status: "ready", settings: result })
      } catch (error) {
        if (ticket === generation) failure(error)
      }
    },
    async save(payload: KehrblattPlanningSettings) {
      if (!api || !current() || !contextPlanningPage(session, organizationId)?.canManage)
        throw new DOMException("Unconfirmed planning write", "InvalidStateError")

      try {
        return await api.save(payload)
      } catch (error) {
        if (
          !current() ||
          [401, 403, 409].includes((error as { statusCode?: number })?.statusCode ?? 0)
        )
          failure(error)

        throw error
      }
    },
    dispose() {
      disposed = true
      capture?.requestOptions.signal.removeEventListener("abort", invalidate)
      invalidate()
    },
  }
}
